/**
 * User Event Relay topic registry (TRD sections 4 and 4.1).
 *
 * A change anywhere under `user-event-relay/` updates the Thrive Desktop Rust
 * mirror in the same change, and the shared fixtures in `fixtures/` are the
 * proof that both sides still agree.
 *
 * Two topics, deliberately. `notification.feed` replaces the clients' feed
 * polling and `reset.offer` carries the contact-centre Reset prompt. Every new
 * topic is a privacy review: a topic name and the fact that it occurred travel
 * in clear through Cloudflare, so a name must never reveal a health event, and
 * a generic topic plus a health-specific discriminator field is forbidden,
 * which is why no envelope carries a `kind`.
 */
import { z } from "zod";
export const RelayTopic = z.enum(["notification.feed", "reset.offer"]);
/**
 * What an envelope on the topic carries. A snapshot carries one notification
 * mutation in clear; a claimable event must be claimed against core-api before
 * the client may display it, and the claim (first writer wins) is what keeps
 * one offer on one screen when several of the member's clients receive it.
 * There is no content-free nudge topic in this iteration.
 */
export const RelayTopicKind = z.enum(["snapshot", "claimable"]);
/**
 * What the producer does with an event the Relay could not deliver (no live
 * socket, or no client acknowledged it in time). The Relay itself keeps
 * nothing. `store` writes the envelope to the producer's undelivered table,
 * where the client collects it on reconnect or by polling; `drop` records the
 * failure and keeps nothing, because the event must never reach a client
 * through a path that cannot claim it.
 */
export const RelayUndeliveredPolicy = z.enum(["store", "drop"]);
/** The client surface that holds a Relay connection. Desktop never brokers for web. */
export const RelaySurface = z.enum(["desktop", "web"]);
/**
 * The Relay broadcasts every event to every one of the member's live sockets,
 * whatever the topic. `surfaces` therefore does not gate delivery; it names
 * the surfaces that act on the topic, which is what the producer stores
 * undelivered rows for (`relayWriteAheadSurfaces`). A surface not listed
 * would still receive the frame and acknowledge it (ack means received, not
 * acted on). Both surfaces act on both topics: any client can claim a Reset
 * offer, and the member's primary client (see `frames.ts`) is the one that
 * does.
 */
export const RELAY_TOPICS = {
    "notification.feed": {
        kind: "snapshot",
        whenUndelivered: "store",
        surfaces: ["desktop", "web"],
    },
    "reset.offer": { kind: "claimable", whenUndelivered: "drop", surfaces: ["desktop", "web"] },
};
/** Topics whose undelivered events the producer stores for a later fetch. */
export function relayTopicsStoredWhenUndelivered() {
    return RelayTopic.options.filter((topic) => RELAY_TOPICS[topic].whenUndelivered === "store");
}
//# sourceMappingURL=topics.js.map